import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateWaiverTemplate } from '../../../hooks/waivers/useCreateWaiverTemplate';
import { useUpdateWaiverTemplate } from '../../../hooks/waivers/useUpdateWaiverTemplate';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import QuillEditor from '../../common/forms/QuillEditor';

export default function WaiverTemplateForm({ successCallback = null, history = null, historyEntry = null, waiverTemplate = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const quillModules = {
      toolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        // Notice: no 'link' here
        ['clean']
      ]
    };

    const [name, setName] = React.useState(waiverTemplate ? waiverTemplate.name : "");
    const [content, setContent] = React.useState(waiverTemplate ? waiverTemplate.content : "");

    const { create } = useCreateWaiverTemplate(successCallback);
    const { update } = useUpdateWaiverTemplate(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(waiverTemplate ? waiverTemplate.name : "");
        setContent(waiverTemplate ? waiverTemplate.content : "");
    }, [waiverTemplate, open]);

    const handleSave = async () => {
        if (!name.trim()) {
            showSnackbar(t('waivers.templates..messages.name_required'), "warning");
            return;
        }
        if (!content.trim()) {
            showSnackbar(t('waivers.templates..messages.content_required'), "warning");
            return;
        }

        try 
        {
            if (mode === "create") {
                await create({ 
                    history_id: history ? history.id : null,
                    history_entry_id: historyEntry ? historyEntry.id : null,
                    name: name,
                    content: content
                });
                showSnackbar(t('waivers.templates.messages.create_success'), "success");
            } else if (mode === "edit" && waiverTemplate) {
                await update(waiverTemplate.id, { 
                    name: name,
                    content: content
                });
                showSnackbar(t('waivers.templates.messages.update_success'), "success");
            }
            setName("");   // clear field after save
            setContent("");   // clear field after save
            onClose();     // close dialog
        } catch (err) {
            showSnackbar(t('waivers.templates.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
        setName("");   // clear field on cancel
        setContent("");   // clear field on cancel
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Waiver Template" : "Edit Waiver Template"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <TextField
                label="Template Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
            />
            <QuillEditor
              modules={quillModules}
              value={content}
              onChange={setContent} 
            />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}